/*
 * The Organizer LINE OA screen's state, shared by the classic panel and the v3 screen.
 *
 * Every action funnels through `run`, which reloads afterwards. That is not tidiness: this screen's
 * status is *derived by the server* from what is configured — adding a channel id can move a
 * connection from `draft` to `verification_pending` — so a local optimistic update would show a
 * status the server does not agree with, on the one screen whose whole job is saying whether the
 * integration works.
 *
 * Secrets are write-only here because they are write-only on the server: there is no endpoint that
 * returns one, and `secretsConfigured` is the entire vocabulary available for describing them. The
 * draft fields are cleared the moment they are sent.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { ApiError, lineOaApi } from '../../api';
import type { EventLineOaDto, LineOaAuditEntryDto, LineOaVerifyDto } from '../../api';

export interface LineOaDraft {
  displayName: string;
  basicId: string;
  addFriendUrl: string;
  messagingChannelId: string;
}

export interface LineOaState {
  view: EventLineOaDto | null;
  loading: boolean;
  busy: boolean;
  error: string;
  loadError: string;
  report: LineOaVerifyDto['report'] | null;
  audit: LineOaAuditEntryDto[] | null;
  draft: LineOaDraft;
  setDraft: Dispatch<SetStateAction<LineOaDraft>>;
  wantsMessaging: boolean;
  setWantsMessaging: Dispatch<SetStateAction<boolean>>;
  secrets: { messagingChannelSecret: string; messagingAccessToken: string };
  setSecrets: Dispatch<SetStateAction<{ messagingChannelSecret: string; messagingAccessToken: string }>>;
  message: string;
  setMessage: Dispatch<SetStateAction<string>>;
  dirty: boolean;
  create: () => void;
  save: () => void;
  rotate: () => void;
  verify: () => Promise<void>;
  setEnabled: (enabled: boolean) => void;
  saveSettings: (patch: { addFriendEnabled?: boolean; message?: string }) => void;
  loadAudit: () => Promise<void>;
  /** The classic panel shows the history behind a toggle; hiding it drops the list. */
  clearAudit: () => void;
}

const EMPTY: LineOaDraft = { displayName: '', basicId: '', addFriendUrl: '', messagingChannelId: '' };

export function useLineOa(token: string, eventSlug?: string): LineOaState {
  const [view, setView] = useState<EventLineOaDto | null>(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<LineOaVerifyDto['report'] | null>(null);
  const [audit, setAudit] = useState<LineOaAuditEntryDto[] | null>(null);
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState<LineOaDraft>(EMPTY);
  const [wantsMessaging, setWantsMessaging] = useState(false);
  const [secrets, setSecrets] = useState({ messagingChannelSecret: '', messagingAccessToken: '' });

  const load = useCallback(async () => {
    try {
      const next = await lineOaApi.get(token, eventSlug);
      setView(next);
      setMessage(next.settings.message);
      if (next.connection) {
        setDraft({
          displayName: next.connection.displayName,
          basicId: next.connection.basicId ?? '',
          addFriendUrl: next.connection.addFriendUrl ?? '',
          messagingChannelId: next.connection.messagingChannelId ?? '',
        });
        setWantsMessaging(next.connection.capability === 'messaging');
      }
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่า LINE OA ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  /** One wrapper so no action can leave a button spinning after it throws. */
  const run = useCallback(async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }, [load]);

  const connectionInput = () => ({
    displayName: draft.displayName,
    basicId: draft.basicId || null,
    addFriendUrl: draft.addFriendUrl || null,
    messagingChannelId: wantsMessaging ? draft.messagingChannelId || null : null,
    capability: (wantsMessaging ? 'messaging' : 'add_friend_only') as 'messaging' | 'add_friend_only',
  });

  const connection = view?.connection ?? null;
  const dirty = !!connection && (
    draft.displayName !== connection.displayName
    || draft.basicId !== (connection.basicId ?? '')
    || draft.addFriendUrl !== (connection.addFriendUrl ?? '')
    || draft.messagingChannelId !== (connection.messagingChannelId ?? '')
    || wantsMessaging !== (connection.capability === 'messaging')
  );

  return {
    view,
    loading: !view && !loadError,
    busy, error, loadError, report, audit,
    draft, setDraft, wantsMessaging, setWantsMessaging, secrets, setSecrets, message, setMessage,
    dirty,
    create: () => void run(() => lineOaApi.create(token, connectionInput(), eventSlug), 'สร้างการตั้งค่าไม่สำเร็จ'),
    save: () => void run(() => lineOaApi.update(token, connectionInput(), eventSlug), 'บันทึกไม่สำเร็จ'),
    rotate: () => void run(async () => {
      await lineOaApi.rotateSecrets(token, {
        ...(secrets.messagingChannelSecret ? { messagingChannelSecret: secrets.messagingChannelSecret } : {}),
        ...(secrets.messagingAccessToken ? { messagingAccessToken: secrets.messagingAccessToken } : {}),
      }, eventSlug);
      // Cleared immediately: there is no reason for a credential to sit in a form field after it
      // has been sent, and the server will never send it back to repopulate one.
      setSecrets({ messagingChannelSecret: '', messagingAccessToken: '' });
    }, 'บันทึก secret ไม่สำเร็จ'),
    verify: async () => {
      setBusy(true);
      setError('');
      try {
        const result = await lineOaApi.verify(token, eventSlug);
        setReport(result.report);
        await load();
      } catch (e) {
        setReport(null);
        setError(e instanceof ApiError ? e.message : 'ตรวจสอบการเชื่อมต่อไม่สำเร็จ');
      } finally {
        setBusy(false);
      }
    },
    setEnabled: (enabled: boolean) =>
      void run(() => lineOaApi.setEnabled(token, enabled, eventSlug), 'เปลี่ยนสถานะไม่สำเร็จ'),
    saveSettings: (patch) =>
      void run(() => lineOaApi.updateSettings(token, patch, eventSlug), 'บันทึกไม่สำเร็จ'),
    clearAudit: () => setAudit(null),
    loadAudit: async () => {
      try {
        setAudit(await lineOaApi.audit(token, eventSlug));
      } catch {
        // An audit list that will not load is not a reason to break the screen it sits under; the
        // panel renders its own "could not load" rather than taking the configuration down with it.
        setAudit([]);
      }
    },
  };
}
